<template>
  <SectionTemplate title="常见问题（FAQs）">
    <XFaq :faq-list="faqList" @contact="handleContact" />
  </SectionTemplate>
</template>

<script setup lang="ts">
import XFaq from '@/design/widgets/faq/faq.vue';
import { useFaqHelper } from '@/design/widgets/faq/useFaqHelper';
import SectionTemplate from '@/design/widgets/landing-section/cn-template.vue';
import { showDynamicContact } from '../../utils/contact';

const linksConfig = {
  'what-is-youyan': 'https://www.youyan3d.com/blog/What-Is-Youyan.html',
  'try-youyan-free': 'https://www.youyan3d.com/login',
  'corporate-training': 'https://www.youyan3d.com/scenarios/corporate-training.html',
  pedagogical: 'https://www.youyan3d.com/scenarios/pedagogical.html',
  marketing: 'https://www.youyan3d.com/scenarios/marketing.html',
  'ai-motion-generation':
    'https://www.youyan3d.com/blog/Text-to-3D-Animation-Generate-AI-Digital-Human-Animation-by-Input-Script.html',
  'ai-voice-generation':
    'https://www.youyan3d.com/blog/Supernatural-Multistyle-Text-to-Language-for-Professional-Scenarios.html',
  'aigc-camera': 'https://www.youyan3d.com/blog/Text-to-3D-Camera-Movement-Auto-Generate.html',
  'we-media': 'https://www.youyan3d.com/scenarios/we-media.html',
  case: 'https://www.youyan3d.com/case',
  template: 'https://www.youyan3d.com/template',
  subscribe: 'https://www.youyan3d.com/subscribe',
  'multi-language-tts':
    'https://www.youyan3d.com/blog/Master-Multilingual-Videos-from-Translation-to-Voice.html',
};

// 将链接配置转换为 FaqLink 格式
const links = Object.entries(linksConfig).map(([key, url]) => ({ key, url }));

// 使用 FAQ 辅助工具
const { getLink, getStrong, getContactLink } = useFaqHelper(links);

// FAQ 数据列表
const faqList = [
  {
    question: '有言是一款什么样的AI视频工具？',
    answer: `魔珐有言是一款零门槛、易上手的${getLink('what-is-youyan', '3D数字人AI视频平台')}。用户通过文本、PPT或模板，即可一键生成高质量3D数字人视频内容。无需出镜、拍摄或后期制作，AI自动生成场景、灯光、人物形象、表现和运镜，效果媲美专业团队。不论企业还是个人，均可轻松满足各种视频需求，快速提升制作效率。
${getLink('try-youyan-free', '立即体验')}，开始你的AI视频创作之旅。`,
    isOpen: false,
  },
  {
    question: 'AI数字人视频和传统视频制作相比有什么优势？',
    answer: `传统视频制作流程复杂，往往涉及脚本撰写、搭建场地、演员拍摄、剪辑包装等多个环节，还需要导演、演员、摄影师、剪辑师等多人协作，周期长、成本高、产能有限，难以满足企业或个人对高频内容的持续需求。
使用魔珐有言，您无需搭建场地、聘请演员和拍摄团队或进行复杂的后期剪辑，只需上传文字或PPT，即可一键生成高质量的AI数字人视频。
与传统方式相比，有言AI数字人视频生成的优势在于：
- ${getStrong('门槛更低')}：无需专业制作技能，人人都能轻松上手
- ${getStrong('效率更高')}：几分钟生成视频，快速响应内容需求
- ${getStrong('成本更可控')}：无需拍摄制作团队，显著降低制作费用
- ${getStrong('更新更灵活')}：支持多语种、多版本批量生成，便于快速迭代
- ${getStrong('质量更统一')}：保障内容输出的标准化和品牌统一性
如果您希望快速生成专业级视频内容，有言是更高效、便捷的解决方案。${getLink('try-youyan-free', '立即注册')}，体验全新的视频创作方式。`,
    isOpen: false,
  },
  {
    question: '魔珐有言相比于数字人平台或其他AI视频平台，有什么技术优势？',
    answer: `魔珐有言通过${getStrong('三维图形学、AI生成以及专业视频技术')}三大核心技术，让用户无需出镜拍摄，无需剪辑，就能轻松创作出高质量的3D数字人视频内容。
${getStrong('在三维图形方面')}，平台基于自研的3D数字人引擎，实现了3D超写实人物的高效建模与绑定、动画生成和物理级渲染，人物皮肤纹理、光影质感、表情动作等细节均达到电影级水准。用户还可自定义角色外观，包括捏脸、妆容、服饰等，及图片生成3D数字人，满足多样化创作需求。
${getStrong('在AI生成能力方面')}，平台融合了${getLink('ai-voice-generation', 'AI语音生成（TTS）')}、${getLink('ai-motion-generation', 'AI动作生成（TTSA）')}、语音克隆（VC）等技术，让AI数字人具备接近真人的表现力。
${getStrong('在专业视频制作方面')}，有言依托专业视频逻辑构建的演播室（Layout）、${getLink('aigc-camera', 'AIGC镜头（Text-to-Camera）')}以及丰富的后期包装能力与视频素材资源，进一步增强了视频专业性。
不同于市面上仅支持换脸合成或口型驱动的AI数字人视频工具，有言真正实现了从视频质量、生成效率到内容可控性的全面升级，真正满足${getLink('corporate-training', '企业培训')}、产品推广、教学讲解、活动发布等各类专业视频创作需求。`,
    isOpen: false,
  },
  {
    question: '有言的AI数字人视频适用于哪些应用场景？',
    answer: `魔珐有言广泛应用于企业培训、产品讲解、教学微课、数字人口播、本地化传播等高频内容场景，帮助企业与创作者高效产出专业级视频内容。例如：
- ${getLink('corporate-training', '企业培训团队')}：通过有言大幅提升课程制作效率，快速生成标准化的培训课程、入职指引、产品讲解等内容；
- ${getLink('pedagogical', '教育机构和学校老师')}：将教材一键转化为教学微课，还能上传至学习平台、参与微课大赛，创新教学方式与课堂体验；
- ${getLink('marketing', '市场团队')}：常用有言批量化数字人口播视频，清晰传递产品与企业价值，有效提升社媒流量与客群触达；
- ${getLink('we-media', '个人创作者')}：无需拍摄剪辑，快速完成高质量视频化表达，创作更自由。
平台提供数千款3D人物角色、海量3D场景、丰富的动作、音色和镜头选择，轻松满足不同行业、不同部门的创作需求。
你可以浏览${getLink('case', '客户案例页')}，了解不同行业的真实应用实践；也可以前往${getLink('template', '作品中心')}，浏览真实用户作品，获取创作灵感。`,
    isOpen: false,
  },
  {
    question: '如何用魔珐有言生成AI数字人视频？',
    answer: `在魔珐有言，仅需${getStrong('3步')}即可完成专业级视频生成：
${getStrong('1. 上传脚本或PPT')}
${getStrong('2. 选择适合的3D数字人形象和3D场景')}
${getStrong('3. 点击"3D生成"，即可一键生成高质量的AI数字人视频')}
${getLink('try-youyan-free', '立即注册体验')}，开启你的AI数字人视频创作之旅。`,
    isOpen: false,
  },
  {
    question: '我可以把PPT一键转成数字人视频吗？',
    answer: `当然可以。有言支持将PPT快速转化为由3D数字人讲解的视频内容，操作非常简单：
${getStrong('1. 上传PPT或PDF文件')}
${getStrong('2. 为每页输入讲解脚本')}
${getStrong('3. 选择3D数字人角色与场景')}
${getStrong('4. 一键生成高质量视频')}
整个过程不需要真人录屏，也不需要剪辑。数字人会自动逐页配合 PPT 内容讲解，语音自然、表情生动、动作贴合语义，比传统PPT配音或真人录制更省时、更标准。
这一功能尤其适合企业培训课件、教育课程、销售讲解方案等 PPT 主导的内容输出场景。相比传统的静态PPT播放或真人录屏讲解，数字人讲解更连贯、表现力更丰富，不仅显著提升内容吸引力，也大大降低了视频制作的人力与时间成本，让信息传递更高效、更专业。
${getLink('try-youyan-free', '立即体验PPT转视频功能')}，让你的内容讲解更高效、更具表现力。`,
    isOpen: false,
  },
  {
    question: '魔珐有言可以免费使用吗？',
    answer: `魔珐有言提供${getLink('try-youyan-free', '免费体验')}，用户可免费体验AI数字人视频生成功能，包括文字转视频、PPT转视频、数字人口播等基础功能。若需生成超清长视频、去除水印、定制专属数字人形象，或满足企业在培训、营销等商用场景下的版权合规要求，可按需升级至${getLink('subscribe', '个人付费版或企业商用套餐')}。`,
    isOpen: false,
  },
  {
    question: '视频可以反复修改吗？怎么更新内容？',
    answer: `当然可以。3D生成视频后，你可以对视频中的任意元素进行精准修改，包括${getStrong('脚本、PPT素材、角色形象、语音语调、动作表情、镜头、场景、包装')}等，并能实时预览修改效果，无需重新拍摄或整条视频重做。
相比传统真人拍摄"一旦定稿就难以改动"的方式，有言彻底解决了"返工重剪"的高成本问题；也区别于市面上大多数 AI 视频工具的"开盲盒式生成"，有言提供了更高的创作可控性与编辑自由度。你可以灵活轻松修改细节、快速更新版本、统一视频风格，轻松应对企业级多版本、多语种、标准化的内容输出的实际需求。
${getLink('try-youyan-free', '立即体验可编辑功能')}，开启高效可控的AI视频创作。`,
    isOpen: false,
  },
  {
    question: '能否一键生成多语言视频？',
    answer: `当然可以。有言支持一键生成多语言数字人视频，帮助企业和个人轻松应对跨语种传播和全球化内容制作需求。
你只需在脚本撰写阶段选择目标语言和语音风格，有言就能自动生成该语言的自然语音配音，并同步驱动数字人的动作、表情与口型，输出流畅、地道的多语种视频内容。无须真人翻译配音，也无需反复剪辑录制，几分钟即可完成。
目前平台支持${getLink('multi-language-tts', '30+主流语种、400+音色风格')}，覆盖英语、法语、德语、西班牙语、阿拉伯语、日语、韩语等多语言场景，并支持多种地区口音（如美式/英式英语、粤语等），适配不同受众的语言偏好。
无论你是做跨境产品推广、海外培训课程、还是多语言客服解答，有言都能大幅降低多语种视频制作门槛，提升效率与本地化表达效果。
👉 立即试试一键多语种视频生成，让你的内容轻松"出海"。`,
    isOpen: false,
  },
  {
    question: '有言生成的视频支持高清导出吗？',
    answer: `当然可以。魔珐有言支持多种分辨率的视频导出，满足从快速预览到专业应用的不同需求：
- ${getStrong('免费体验用户')}可生成 ${getStrong('540P')} 视频，适合快速体验数字人效果；
- ${getStrong('个人付费用户')}支持导出 ${getStrong('1080P 高清视频')}，满足日常教学、内部培训、线上传播等主流视频需求；
- ${getStrong('企业客户')}可解锁 ${getStrong('最高 4K 超高清分辨率')}导出，支持线下发布会、展览展示、大屏讲解等专业场景，确保视觉质量达到电影级水准。
无论是用于移动端观看，还是用于大屏播出，有言都能为你生成高质量、高精度的视频内容，真正实现"无需拍摄，也能轻松做出专业视频"。
欢迎${getLink('try-youyan-free', '立即注册体验')}，了解更多导出能力与分辨率选项。`,
    isOpen: false,
  },
  {
    question: '有言支持API调用/SDK封装吗？',
    answer: `有言平台可提供一系列API和SDK接入服务，我们已为多家大型机构（如国有银行、头部券商等）提供稳定的API服务，助力其高效生产专业视频内容，并实现业务流程中的深度融合。
如您有相关部署需求，或希望了解更多信息，欢迎${getContactLink('api-sdk', '联系专属服务顾问')}，获取1对1接入咨询与技术演示支持。`,
    isOpen: false,
  },
];

// 处理联系方式点击
const handleContact = (action: string) => {
  if (action === 'contact') {
    showDynamicContact(1078922, '企业级数字人定制', '1V1专属咨询服务');
  } else if (action === 'api-sdk') {
    showDynamicContact(1431230, 'API/SDK接入咨询', '1对1技术演示支持');
  }
};
</script>
